import { useEffect, useRef } from 'react'
import classNames from 'classnames'
import { motion } from 'framer-motion'
import { LuLayers, LuX } from 'react-icons/lu'
import {
    CATEGORY_META,
    CATEGORY_ORDER,
    PERIODS,
    PERIOD_PHRASE,
} from './categories'

interface FilterBarProps {
    period?: TInboxPeriod
    category?: TInboxCategory
    onPeriod: (period: TInboxPeriod | undefined) => void
    onCategory: (category: TInboxCategory | undefined) => void
    /** Chips that could only ever open an empty list for this member. */
    hidden?: readonly TInboxCategory[]
}

/**
 * «کی» and «چه نوع», in that order: a segmented control for the time window,
 * then a row of type chips that scrolls sideways on a phone and wraps on a
 * laptop. The selected period is one green pill that slides between options.
 */
export function FilterBar({
    period,
    category,
    onPeriod,
    onCategory,
    hidden = [],
}: FilterBarProps) {
    const chipsRef = useRef<HTMLDivElement | null>(null)
    const shown = CATEGORY_ORDER.filter((key) => !hidden.includes(key))

    // A filter restored from the URL may sit off-screen in the chip row.
    useEffect(() => {
        const row = chipsRef.current
        const active = row?.querySelector<HTMLElement>('[aria-pressed="true"]')
        active?.scrollIntoView({ block: 'nearest', inline: 'center' })
        // Only on mount; a tap already brings its chip into view.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    return (
        <div className="flex flex-col gap-2.5 rounded-card border border-divider bg-white p-2 shadow-badge md:p-2.5">
            <div
                role="radiogroup"
                aria-label="بازهٔ زمانی"
                className="grid grid-cols-4 gap-1 rounded-xl bg-canvas p-1"
            >
                {PERIODS.map(({ key, label }) => {
                    const active = (period ?? 'all') === key
                    return (
                        <button
                            key={key}
                            type="button"
                            role="radio"
                            aria-checked={active}
                            onClick={() =>
                                onPeriod(key === 'all' ? undefined : key)
                            }
                            className={classNames(
                                'relative h-8 rounded-lg text-[12px] font-bold transition-colors duration-200 md:h-9 md:text-[13px]',
                                active
                                    ? 'text-white'
                                    : 'text-text-secondary active:text-heading'
                            )}
                        >
                            {active && (
                                <motion.span
                                    layoutId="messages-period-pill"
                                    className="absolute inset-0 rounded-lg bg-green-1 shadow-fab"
                                    transition={{
                                        type: 'spring',
                                        stiffness: 480,
                                        damping: 36,
                                    }}
                                />
                            )}
                            <span className="relative">{label}</span>
                        </button>
                    )
                })}
            </div>

            <div
                ref={chipsRef}
                className="-mx-2 flex gap-1.5 overflow-x-auto px-2 pb-0.5 [scrollbar-width:none] md:mx-0 md:flex-wrap md:overflow-visible md:px-0 [&::-webkit-scrollbar]:hidden"
            >
                <Chip
                    active={!category}
                    onClick={() => onCategory(undefined)}
                    className="border-heading bg-heading text-white"
                >
                    <LuLayers className="h-3.5 w-3.5" />
                    همه
                </Chip>
                {shown.map((key) => {
                    const { label, Icon, chip } = CATEGORY_META[key]
                    return (
                        <Chip
                            key={key}
                            active={category === key}
                            onClick={() =>
                                onCategory(category === key ? undefined : key)
                            }
                            className={chip}
                        >
                            <Icon className="h-3.5 w-3.5" />
                            {label}
                        </Chip>
                    )
                })}
            </div>
        </div>
    )
}

function Chip({
    active,
    onClick,
    className,
    children,
}: {
    active: boolean
    onClick: () => void
    /** The colours of the selected chip. */
    className: string
    children: React.ReactNode
}) {
    return (
        <motion.button
            type="button"
            aria-pressed={active}
            onClick={onClick}
            whileTap={{ scale: 0.93 }}
            className={classNames(
                'flex h-8 shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full border px-3 text-[12px] font-bold transition-colors duration-200 md:text-[13px]',
                active
                    ? className
                    : 'border-stroke bg-white text-text-secondary'
            )}
        >
            {children}
        </motion.button>
    )
}

/**
 * What is on screen, in words, and the way back to everything. Only shown
 * while a filter is on.
 */
export function FilterSummary({
    period,
    category,
    onClear,
}: {
    period?: TInboxPeriod
    category?: TInboxCategory
    onClear: () => void
}) {
    if (!period && !category) return null
    const what = category
        ? `پیام‌های «${CATEGORY_META[category].label}»`
        : 'همهٔ پیام‌ها'
    return (
        <motion.div
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex items-center justify-between gap-2 px-1"
        >
            <p className="min-w-0 truncate text-[12px] text-text-secondary md:text-[13px]">
                <span className="font-bold text-heading">{what}</span>
                {period && ` ${PERIOD_PHRASE[period]}`}
            </p>
            <button
                type="button"
                onClick={onClear}
                className="flex h-7 shrink-0 items-center gap-1 rounded-full bg-divider px-2.5 text-[11px] font-bold text-gray-1 transition-colors active:bg-stroke md:text-xs"
            >
                <LuX className="h-3.5 w-3.5" />
                حذف فیلترها
            </button>
        </motion.div>
    )
}
